<template>
  <VCard
    class="category-data-component"
  >
    <template #header>
      <div class="title">
        {{ editMode ? initialName : 'Создание' }}
      </div>

      <div class="description">
        Тип: {{ productCategory.isLeaf ? 'Товар' : 'Категория' }}
      </div>

      <div
        v-if="!isRoot && productCategory.parent"
        class="description"
      >
        Родительская категория: {{ productCategory.parent.name }}
      </div>
    </template>

    <VForm ref="form">
      <template v-if="productCategory.isLeaf === false && !isRoot">
        <VSelect
          v-model="productCategory.parent"
          label="Родительская категория"
          :options="parentProductCategoryOptions"
          :disabled="editMode"
          :search-function="searchCategory"
          required
        />

        <VSelect
          label="Тип"
          disabled
          :options="productCategoryTypeList"
          v-model="productCategoryType"
        />
      </template>

      <VInput
        ref="nameInput"
        v-model="productCategory.name"
        label="Название"
        label-width="200px"
        required
        :validate-method="validateProductCategoryName"
      />

      <VSwitch
        v-if="productCategory.isLeaf === false"
        v-model="productCategory.allowsProducts"
        label="Допускает товары"
        theme="slider"
      />

      <VSwitch
        v-if="productCategory.isLeaf === true || productCategory.allowsProducts === true"
        :disabled="editMode === false && Boolean(productCategory.parent)"
        v-model="productCategory.isService"
        label="Услуга"
        theme="slider"
      />

      <template v-if="productCategory.isLeaf === true || productCategory.allowsProducts === true">
        <VFormItem
          key="payment-delay"
          label="Отсрочка платежа"
          label-width="200px"
          with-asterisk
        >
          <VInputNumber
            v-model="productCategory.paymentDelay"
            required
            :precision="0"
            :max="1000"
          />

          <span>дней</span>
        </VFormItem>

        <VInputPercentage
            key="price-mod-transport"
            label="Транспортная наценка"
            label-width="200px"
            with-asterisk
            v-model="productCategory.priceModTransport"
            :min="0"
            required
            :type="Multiplier"
        />

        <VInputPercentage
            key="price-mod-profit"
            label="Наценка Платформы"
            label-width="200px"
            with-asterisk
            v-model="productCategory.priceModProfit"
            :min="0"
            required
            :type="Multiplier"

        />

        <VSelect
          v-model="productCategory.vatRate"
          label="Размер НДС"
          label-width="200px"
          :options="[...$core.dictionaries.vatRates]"
          :clearable="false"
          required
        />
        <VFormItem
          key="id-techno"
          label="ID техно"
          label-width="200px"
        >
          <VInputNumber
            v-model="productCategory.externalId"
            :min="1"
          />
        </VFormItem>

      </template>
    </VForm>

    <template #footer>
      <VButton
        theme="primary"
        @click="saveProductCategory"
      >
        {{ editMode === false ? 'Создать' : 'Сохранить' }}
      </VButton>
      <VButton
        v-if="editMode"
        :disabled="publishDisabledReason !== null"
        :title="publishDisabledReason"
        @click="changeProductCategoryPublishState"
      >
        {{ productCategory.isPublished ? 'Снять с публикации' : 'Опубликовать' }}
      </VButton>
    </template>
  </VCard>
</template>

<style lang="scss" src="./ProductCategoryForm.scss"/>

<script lang="ts">

import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';
import ProductCategory          from "@/domains/ProductCategories/ProductCategory";
import Multiplier               from "@/core/types/Multiplier";

import VInputPercentage         from "@/lib/components/VInputPercentage/VInputPercentage.vue";

export default {
  name:       'ProductCategoryForm',
  components: {VInputPercentage},

  props: {
    productCategory: {
      type:    Object as PropType<ProductCategory>,
      default: null,
    },
  },

  emits: ['update:categoryData'],

  setup() {
    return {
      Multiplier
    };
  },

  data() {
    return {
      parentProductCategoryOptions: [],
      initialName:                  this.productCategory.name,
      productCategoryTypeList:      [
        {
          id:   'leaf',
          name: 'Товар',
        },
        {
          id:   'notLeaf',
          name: 'Категория',
        },
      ],
    };
  },

  computed: {
    editMode() {
      return Boolean(this.productCategory.id);
    },

    isRoot() {
      return !this.productCategory.parentId;
    },

    /** Товар нельзя опубликовать без единиц измерения. null — можно. */
    publishDisabledReason(): string | null {
      if (
        this.productCategory.isPublished === false
        && (this.productCategory.isLeaf === true || this.productCategory.allowsProducts === true)
        && this.$core.utils.isNotEmptyArray(this.productCategory.allowedUnits) === false
      ) {
        return 'Нельзя опубликовать товар без единиц измерения';
      }
      else {
        return null;
      }
    },

    productCategoryType() {
      return this.productCategory.isLeaf
        ? this.productCategoryTypeList[0]
        : this.productCategoryTypeList[1];
    },
  },

  mounted() {
    /** Когда мы делаем копию, а флаг this.editMode === false говорит о том что сейчас мы делаем копию
     * нужно показывать пользователю что есть ошибка уникальности в имени */
    if (this.editMode === false && this.productCategory.name) {
      this.$refs.nameInput.validateForce();
    }

    if (this.editMode === false && this.productCategory.parent) {
      this.productCategory.isService = this.productCategory.parent.isService;
    }
  },

  methods: {
    /** Ветки add/update разделены сознательно: у Service.add и Service.update
     * разные контракты возврата (ProductCategory | null и boolean),
     * объединять их в одну переменную method было некорректно.
     * При ошибке RPC сервис возвращает null/false (тост показывает глобальный
     * обработчик) — в этом случае эмит успеха не делаем, форма остаётся открытой. */
    async saveProductCategory() {
      await this.$refs.form.validate();

      if (this.$refs.form.isValid === false) {
        // форма невалидна — ошибки показала сама форма
      }
      else if (this.editMode === true) {
        const updated = await ProductCategoriesService.update(this.productCategory);

        if (updated === true) {
          this.$emit('update:categoryData', this.productCategory);
        }
      }
      else {
        const created = await ProductCategoriesService.add(this.productCategory);

        if (this.$core.utils.isNullOrUndefined(created) === false) {
          this.$emit('update:categoryData', created);
        }
      }
    },

    async changeProductCategoryPublishState() {
      this.productCategory.isPublished = !this.productCategory.isPublished;

      const updateResult = await ProductCategoriesService.update(this.productCategory);

      if (updateResult === true) {
        this.$emit('update:categoryData', this.productCategory);
      }
    },

    validateProductCategoryName(value) {
      if (typeof value !== 'string' || value.length === 0) {
        return null;
      }

      if (this.editMode && value === this.initialName) {
        return null;
      }

      return new Promise(resolve => {

        /** showUnpublished: true обязателен — DAO.add создаёт категории
         * с is_published=false, а getList без этого флага фильтрует
         * только опубликованные. Без флага валидация не видела дубли
         * среди неопубликованных, и сабмит падал на уникальном индексе БД. */

        ProductCategoriesService.getList({ name: value, showUnpublished: true })
          .then((data) => {
            if (this.$core.utils.isNotEmptyArray(data)) {
              resolve('Ошибка уникальности. Данное название уже существует');
            } else {
              resolve(null);
            }
          });

      });
    },

    async searchCategory(pattern) {
      return await ProductCategoriesService.getList({ pattern, isLeaf: false });
    },
  },
};
</script>